import React, { useState, useEffect } from "react";
import styles from "./ReportGrid.module.scss";
import { DroppableBox } from "src/components/DropableBox/DroppableBox";
import {
  CHART_DATE_FORMAT,
  useGlobalCurrency,
  usePortfolioInfo,
  useReportBespoke,
  useStructureGrouping,
} from "@iliotech/data-wire";
import { observer } from "mobx-react-lite";
import classNames from "classnames";
import * as widgets from "../extractedComponents/index";
import { IoClose } from "react-icons/io5";
import {
  AlignmentType,
  IReportCell,
} from "@iliotech/data-wire/src/stores/ReportBespoke/ReportBespokeStore";
import { ReportCellLayoutManagerSelect } from "../components/ReportCellLayoutManager/ReportCellLayoutManagerSelect";
import { ReportWidgetProps } from "../extractedComponents/ReportWidgetProps";
import { ReportCellSettingsSelect } from "../components/ReportCellSettings/ReportCellSettingsSelect";

import { useParams } from "react-router-dom";
import { useFakePeriodContext } from "../hooks/useFakePeriodContext";
import { getAlignmentStyles } from "./utils";
import { useFakeStructureContext } from "../hooks/useFakeStructureContext";
import { ReportCellSlidersSelect } from "../components/ReportCellSliders/ReportCellSlidersSelect";
import { PositionChooser } from "../components/ReportCellColumnChooser/PositionChooser";
import { TransactionChoooser } from "../components/ReportCellColumnChooser/TransactionChooser";

type IdType = keyof typeof widgets;
interface ReportGridProps {
  pageId: string;
  rows?: number;
  columns?: number;
  gridGap?: number;
  isLandscape?: boolean;
}

const TrackedDroppableBox: React.FC<any> = observer(
  ({ children, ...props }) => {
    const store = useReportBespoke();
    const { currency } = useGlobalCurrency();
    const portfolioId = useParams().portfolioId;
    const { data: portfolioInfo } = usePortfolioInfo(portfolioId!);
    const benchmarkName = portfolioInfo?.data?.benchmarkIndex.name;
    const inceptionDate = portfolioInfo?.data?.inceptionDate;
    const generating = store.generating;
    const componentId: IdType = store.getComponentId(props.id) as IdType;
    const needsSliders = componentId === "ScenarioManagerWidget";
    const isPositionWidget = componentId === "PositionsWidget";
    const isTransactionWidget = componentId === "TransactionsWidget";
    const Component: React.FC<ReportWidgetProps> | null = componentId
      ? widgets[componentId]
      : null;
    const cell = store.pages[props.pageId].cells[props.cellId];
    const height = cell.chartHeight;
    const width = cell.chartWidth;
    const dimensions = store.getDimensions();
    const isHalfHeight = height === dimensions.HALF_HEIGHT;
    const isHalfWidth = width === dimensions.HALF_WIDTH;
    const { periodContext, setCellPeriod } = useFakePeriodContext({
      cell,
      store,
      inceptionDateOverride: inceptionDate,
      ...props,
    });
    const { structureGrouping, setCellGrouping, setCellGroup } =
      useFakeStructureContext({
        cell,
        store,
        ...props,
      });
    const removeComponent = () => {
      store.removeCellComponent(props.pageId, props.cellId);
      const cell = document.querySelector(
        `[data-cell-id="${props.id}"]`
      ) as HTMLDivElement;
      if (cell) {
        cell.classList.remove("populated");
        cell.style.opacity = "0.7";
        cell.style.backgroundColor = "rgb(215, 221, 230)";
      }
    };

    return (
      <DroppableBox {...props}>
        {!generating && (
          <div className={styles.row}>
            {isPositionWidget && (
              <PositionChooser cellId={props.cellId} pageId={props.pageId} />
            )}
            {isTransactionWidget && (
              <TransactionChoooser
                cellId={props.cellId}
                pageId={props.pageId}
              />
            )}
            {needsSliders && (
              <ReportCellSlidersSelect
                cellId={props.cellId}
                pageId={props.pageId}
              />
            )}
            <ReportCellSettingsSelect
              {...periodContext}
              onPeriodSelected={setCellPeriod}
              structureContext={structureGrouping}
              inceptionDateOverride={inceptionDate}
              setGrouping={setCellGrouping}
              setSelectedGroup={setCellGroup}
            />

            <ReportCellLayoutManagerSelect
              pageId={props.pageId}
              cellId={props.cellId}
            />
            <div className={styles.remove} onClick={removeComponent}>
              <IoClose size={20} />
            </div>
          </div>
        )}
        {Component ? (
          <div
            style={{
              display: "flex",
              height: "100%",
              width: "100%",
              ...getAlignmentStyles(cell),
            }}
          >
            <Component
              isLandScape={props.isLandscape}
              portfolioId={portfolioId!}
              periodContext={periodContext}
              structureContext={structureGrouping}
              height={height}
              width={width}
              isHalfHeight={isHalfHeight}
              isHalfWidth={isHalfWidth}
              pageId={props.pageId}
              currency={currency}
              benchmarkName={benchmarkName}
              cellId={props.cellId}
            />
          </div>
        ) : (
          "Drop here"
        )}
      </DroppableBox>
    );
  }
);

export const ReportGrid: React.FC<ReportGridProps> = observer(
  ({ isLandscape, pageId }) => {
    const store = useReportBespoke();
    const page = store.pages[pageId];
    if (!page) return null;

    return (
      <div
        className={classNames(
          styles.container,
          isLandscape && styles.landscape
        )}
      >
        <div
          className={classNames(styles.grid, isLandscape && styles.landscape)}
        >
          {Object.entries(page.cells).map(([cellId, cell]) => (
            <TrackedDroppableBox
              isLandscape={isLandscape}
              key={cellId}
              data-cell-id={`${pageId}-${cellId}`}
              id={`${pageId}-${cellId}`}
              pageId={pageId}
              cellId={cellId}
              className={classNames(styles.cell, `${pageId}-${cellId}`)}
              componentId={cell.componentId}
              style={{
                position: cell.position,
                left: cell.left,
                top: cell.top,
                width: `${cell.width}%`,
                height: `${cell.height}%`,
              }}
            />
          ))}
        </div>
      </div>
    );
  }
);
